import { useState, useEffect } from "react";
import { Mail, Phone, Eye, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const getAuthHeaders = () => {
    const auth = sessionStorage.getItem("adminAuth");
    return {
      "Authorization": `Basic ${auth}`,
      "Content-Type": "application/json"
    };
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await fetch(`${API}/admin/inquiries`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInquiries(data);
      }
    } catch (error) {
      console.error("Error fetching inquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (inquiry) => {
    setSelectedInquiry(inquiry);
    setViewDialogOpen(true);
    
    // Mark as read if unread
    if (!inquiry.read) {
      try {
        await fetch(`${API}/admin/inquiries/${inquiry.id}/read`, {
          method: "PUT",
          headers: getAuthHeaders()
        });
        fetchInquiries();
      } catch (error) {
        console.error("Error marking as read:", error);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedInquiry) return;

    try {
      const response = await fetch(`${API}/admin/inquiries/${selectedInquiry.id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success("Inquiry deleted successfully");
        fetchInquiries();
      } else {
        toast.error("Failed to delete inquiry");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
    setDeleteDialogOpen(false);
    setSelectedInquiry(null);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  return (
    <AdminLayout title="Inquiries">
      <div className="space-y-6" data-testid="admin-inquiries-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-earth-stone">
            Customer inquiries from the contact form
          </p>
          <Badge variant="outline" className="text-mystic-amethyst border-mystic-amethyst/30">
            {inquiries.filter(i => !i.read).length} unread
          </Badge>
        </div>

        {/* Inquiries Table */}
        <div className="glass rounded-xl border border-white/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-white/5">
                <TableHead>Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-mystic-mist">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : inquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-mystic-mist">
                    No inquiries yet. They will appear here when customers submit the contact form.
                  </TableCell>
                </TableRow>
              ) : (
                inquiries.map((inquiry) => (
                  <TableRow 
                    key={inquiry.id} 
                    className={!inquiry.read ? "bg-mystic-amethyst/10" : ""}
                    data-testid={`inquiry-row-${inquiry.id}`}
                  >
                    <TableCell>
                      {inquiry.read ? (
                        <Badge variant="outline" className="text-mystic-cyan border-mystic-cyan/30">
                          <Check className="w-3 h-3 mr-1" />
                          Read
                        </Badge>
                      ) : (
                        <Badge className="bg-mystic-orchid border-0">
                          New
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-mystic-silver">
                      {inquiry.name}
                    </TableCell>
                    <TableCell>
                      <a 
                        href={`mailto:${inquiry.email}`}
                        className="text-mystic-mist hover:text-mystic-amethyst flex items-center gap-1"
                      >
                        <Mail className="w-4 h-4" />
                        {inquiry.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      {inquiry.phone ? (
                        <a 
                          href={`tel:${inquiry.phone}`}
                          className="text-mystic-mist hover:text-mystic-amethyst flex items-center gap-1"
                        >
                          <Phone className="w-4 h-4" />
                          {inquiry.phone}
                        </a>
                      ) : (
                        <span className="text-mystic-mist/50">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-mystic-mist text-sm">
                      {formatDate(inquiry.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(inquiry)}
                          className="h-8 w-8 p-0"
                          data-testid={`view-inquiry-${inquiry.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedInquiry(inquiry);
                            setDeleteDialogOpen(true);
                          }}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-inquiry-${inquiry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Inquiry Details</DialogTitle>
            </DialogHeader>
            {selectedInquiry && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-earth-stone mb-1">Name</p>
                    <p className="font-medium text-earth-charcoal">{selectedInquiry.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-earth-stone mb-1">Date</p>
                    <p className="text-earth-charcoal">{formatDate(selectedInquiry.created_at)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-earth-stone mb-1">Email</p>
                  <a 
                    href={`mailto:${selectedInquiry.email}`}
                    className="text-earth-forest hover:underline"
                  >
                    {selectedInquiry.email}
                  </a>
                </div>
                {selectedInquiry.phone && (
                  <div>
                    <p className="text-sm text-earth-stone mb-1">Phone</p>
                    <a 
                      href={`tel:${selectedInquiry.phone}`}
                      className="text-earth-forest hover:underline"
                    >
                      {selectedInquiry.phone}
                    </a>
                  </div>
                )}
                <div>
                  <p className="text-sm text-earth-stone mb-1">Message</p>
                  <div className="p-4 bg-earth-paper rounded-lg">
                    <p className="text-earth-charcoal whitespace-pre-wrap">
                      {selectedInquiry.message}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t">
                  <a 
                    href={`mailto:${selectedInquiry.email}?subject=Re: Your Crystal Haven Inquiry`}
                    className="flex-1"
                  >
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                      <Mail className="w-4 h-4 mr-2" />
                      Reply via Email
                    </Button>
                  </a>
                  {selectedInquiry.phone && (
                    <a 
                      href={`https://wa.me/${selectedInquiry.phone.replace(/[^0-9]/g, '')}?text=Hi ${selectedInquiry.name}! Thank you for your inquiry at Crystal Haven.`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" className="border-whatsapp text-whatsapp hover:bg-whatsapp hover:text-white">
                        WhatsApp
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Inquiry</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this inquiry from {selectedInquiry?.name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-inquiry-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminInquiries;
